import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

const features = {
  start: [
    'Modern one-page website',
    'Mobile friendly',
    'Contact form',
    'WhatsApp / Viber',
    'Map & social media',
    'Basic SEO',
    '5–7 day delivery'
  ],
  business: [
    'Up to five pages',
    'Services / gallery',
    'Contact form',
    'Basic SEO',
    'Google Maps',
    'Social media',
    'Basic owner training'
  ],
  shop: [
    'Categories & products',
    'Cart & ordering',
    'Admin panel',
    'Mobile version',
    'Email confirmations',
    'Basic training'
  ]
};

export default function WebServicesPage() {
  const { t, i18n } = useTranslation();

  const [formStatus, setFormStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const L = (i18n.resolvedLanguage || 'en').slice(0, 2);

  const ft = (x) => {
    const sr = {
      'Modern one-page website': 'Moderna jednostranična stranica',
      'Mobile friendly': 'Prilagođena telefonima',
      'Contact form': 'Kontakt forma',
      'WhatsApp / Viber': 'WhatsApp / Viber',
      'Map & social media': 'Mapa i društvene mreže',
      'Basic SEO': 'Osnovna Google optimizacija',
      '5–7 day delivery': 'Rok izrade 5–7 dana',

      'Up to five pages': 'Do pet odvojenih stranica',
      'Services / gallery': 'Galerija proizvoda ili usluga',
      'Google Maps': 'Google mapa',
      'Social media': 'Povezivanje društvenih mreža',
      'Basic owner training': 'Osnovna obuka vlasnika',

      'Categories & products': 'Kategorije i proizvodi',
      'Cart & ordering': 'Korpa i naručivanje',
      'Admin panel': 'Administratorski panel',
      'Mobile version': 'Mobilna verzija',
      'Email confirmations': 'E-mail potvrde',
      'Basic training': 'Osnovna obuka'
    };

    const de = {
      'Modern one-page website': 'Moderne One-Page-Website',
      'Mobile friendly': 'Für Smartphones optimiert',
      'Contact form': 'Kontaktformular',
      'WhatsApp / Viber': 'WhatsApp / Viber',
      'Map & social media': 'Karte & soziale Netzwerke',
      'Basic SEO': 'Grundlegende SEO-Optimierung',
      '5–7 day delivery': 'Fertigstellung in 5–7 Tagen',

      'Up to five pages': 'Bis zu fünf Unterseiten',
      'Services / gallery': 'Leistungen / Galerie',
      'Google Maps': 'Google Maps',
      'Social media': 'Social-Media-Verknüpfung',
      'Basic owner training': 'Grundlegende Einweisung',

      'Categories & products': 'Kategorien & Produkte',
      'Cart & ordering': 'Warenkorb & Bestellung',
      'Admin panel': 'Administrationsbereich',
      'Mobile version': 'Mobile Version',
      'Email confirmations': 'E-Mail-Bestätigungen',
      'Basic training': 'Grundlegende Einweisung'
    };

    return L === 'sr'
      ? sr[x] || x
      : L === 'de'
      ? de[x] || x
      : x;
  };

  const packs = [
    ['start', '249', features.start],
    ['business', '399', features.business],
    ['shop', '599', features.shop]
  ];

  const getMessage = (type) => {
    const messages = {
      sr: {
        sending: 'Slanje upita...',
        success: 'Upit je uspješno poslan! Javiću vam se uskoro.',
        error: 'Došlo je do greške prilikom slanja. Pokušajte ponovo.',
        required: 'Molimo unesite ime, email i poruku.'
      },
      en: {
        sending: 'Sending request...',
        success: 'Your request has been sent successfully! I will get back to you soon.',
        error: 'An error occurred while sending. Please try again.',
        required: 'Please enter your name, email and message.'
      },
      de: {
        sending: 'Anfrage wird gesendet...',
        success: 'Ihre Anfrage wurde erfolgreich gesendet! Ich melde mich bald bei Ihnen.',
        error: 'Beim Senden ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
        required: 'Bitte geben Sie Name, E-Mail und Nachricht ein.'
      }
    };

    return messages[L]?.[type] || messages.en[type];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const form = e.currentTarget;
    const raw = new FormData(form);

    const name = raw.get('name')?.toString().trim();
    const company = raw.get('company')?.toString().trim();
    const email = raw.get('email')?.toString().trim();
    const type = raw.get('type')?.toString().trim();
    const budget = raw.get('budget')?.toString().trim();
    const message = raw.get('message')?.toString().trim();

    if (!name || !email || !message) {
        setFormStatus(getMessage('required'));
        return;
    }

    setIsSubmitting(true);
    setFormStatus(getMessage('sending'));

    const finalMessage = `
    Naziv firme: ${company || '-'}
    Vrsta projekta: ${type || '-'}
    Budžet: ${budget || '-'}

    Poruka:
    ${message}
    `.trim();

    const payload = {
        name,
        email,
        subject: `Upit za izradu web stranice - ${company || name}`,
        message: finalMessage
    };

    try {
        const response = await fetch('/includes/send_contact.php', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
        });

        const data = await response.json();

        if (data.success) {
        setFormStatus(getMessage('success'));
        form.reset();
        } else {
        setFormStatus(data.message || getMessage('error'));
        }
    } catch (error) {
        console.error('Form submit error:', error);
        setFormStatus(getMessage('error'));
    } finally {
        setIsSubmitting(false);
    }
    };

  return (
    <div className="min-h-screen bg-dark text-white">
      <LanguageSwitcher />

      <header className="container mx-auto px-4 py-6">
        <a href="/" className="font-bold text-xl">
          DS{' '}
          <span className="text-primary">
            / {t('web.navPortfolio')}
          </span>
        </a>
      </header>

      <main>
        <section className="container mx-auto px-4 py-20 md:py-28 text-center">
          <div className="text-primary font-bold tracking-widest text-sm mb-5">
            {t('web.eyebrow')}
          </div>

          <h1 className="text-4xl md:text-7xl font-bold max-w-5xl mx-auto leading-tight">
            {t('web.title')}
          </h1>

          <p className="text-gray-400 text-lg md:text-xl max-w-3xl mx-auto mt-6">
            {t('web.lead')}
          </p>

          <div className="flex gap-3 justify-center flex-wrap mt-8">
            <a
              href="#kontakt"
              className="px-7 py-3 rounded-full bg-gradient-to-r from-primary to-secondary font-bold"
            >
              {t('web.estimate')}
            </a>

            <a
              href="/#projects"
              className="px-7 py-3 rounded-full glass-effect"
            >
              {t('web.work')}
            </a>
          </div>
        </section>

        <section className="container mx-auto px-4 py-16">
          <h2 className="text-3xl md:text-5xl font-bold text-center">
            {t('web.packages')}
          </h2>

          <p className="text-gray-400 text-center mt-3">
            {t('web.packagesSub')}
          </p>

          <div className="grid lg:grid-cols-3 gap-6 mt-12">
            {packs.map(([k, price, list], i) => (
              <div
                key={k}
                className={`glass-effect rounded-3xl p-7 ${
                  i === 1 ? 'ring-2 ring-primary' : ''
                }`}
              >
                {i === 1 && (
                  <div className="text-primary text-xs font-bold mb-3">
                    {t('web.popular')}
                  </div>
                )}

                <h3 className="text-2xl font-bold">
                  {t(`web.${k}`)}
                </h3>

                <p className="text-gray-400 mt-2 min-h-12">
                  {t(`web.${k}Desc`)}
                </p>

                <div className="text-4xl font-bold mt-6">
                  <small className="text-base text-gray-400">
                    {t('web.from')}{' '}
                  </small>
                  {price} EUR
                </div>

                <ul className="space-y-3 mt-6">
                  {list.map((x) => (
                    <li key={x}>
                      ✓ {ft(x)}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <p className="text-gray-400 max-w-3xl mx-auto text-center mt-8">
            {t('web.final')}
          </p>
        </section>

        <section className="container mx-auto px-4 py-16">
          <h2 className="text-3xl md:text-5xl font-bold text-center">
            {t('web.process')}
          </h2>

          <div className="grid md:grid-cols-4 gap-5 mt-10">
            {[1, 2, 3, 4].map((n) => (
              <div
                className="glass-effect rounded-2xl p-6"
                key={n}
              >
                <b className="text-primary">0{n}</b>

                <h3 className="font-bold text-xl mt-3">
                  {t(`web.p${n}`)}
                </h3>

                <p className="text-gray-400 mt-2">
                  {t(`web.p${n}d`)}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="container mx-auto px-4 py-16">
          <h2 className="text-3xl md:text-5xl font-bold text-center">
            {t('web.faq')}
          </h2>

          <div className="max-w-3xl mx-auto mt-10 space-y-4">
            {[1, 2, 3].map((n) => (
              <details
                className="glass-effect rounded-2xl p-5"
                key={n}
              >
                <summary className="font-bold cursor-pointer">
                  {t(`web.faq${n}`)}
                </summary>

                <p className="text-gray-400 mt-3">
                  {t(`web.faq${n}a`)}
                </p>
              </details>
            ))}
          </div>
        </section>

        <section
          id="kontakt"
          className="container mx-auto px-4 py-20"
        >
          <div className="max-w-3xl mx-auto glass-effect rounded-3xl p-7 md:p-10">
            <h2 className="text-3xl font-bold">
              {t('web.contact')}
            </h2>

            <p className="text-gray-400 mt-2">
              {t('web.contactSub')}
            </p>

            <form
              onSubmit={handleSubmit}
              className="grid md:grid-cols-2 gap-4 mt-7"
            >
              <input
                name="name"
                required
                placeholder={t('web.name')}
                className="bg-white/5 border border-white/10 rounded-xl p-3"
              />

              <input
                name="company"
                placeholder={t('web.company')}
                className="bg-white/5 border border-white/10 rounded-xl p-3"
              />

              <input
                name="email"
                type="email"
                required
                placeholder={t('web.email')}
                className="bg-white/5 border border-white/10 rounded-xl p-3"
              />

              <select
                name="type"
                defaultValue=""
                className="bg-dark border border-white/10 rounded-xl p-3"
              >
                <option value="" disabled>
                  {t('web.type')}
                </option>

                <option value="Start">
                  Start
                </option>

                <option value="Business">
                  Business
                </option>

                <option value="Webshop">
                  Webshop
                </option>
              </select>

              <select
                name="budget"
                defaultValue=""
                className="bg-dark border border-white/10 rounded-xl p-3 md:col-span-2"
              >
                <option value="" disabled>
                  {t('web.budget')}
                </option>

                <option value={t('web.budget1')}>
                  {t('web.budget1')}
                </option>

                <option value={t('web.budget2')}>
                  {t('web.budget2')}
                </option>

                <option value={t('web.budget3')}>
                  {t('web.budget3')}
                </option>

                <option value={t('web.budget4')}>
                  {t('web.budget4')}
                </option>
              </select>

              <textarea
                name="message"
                required
                rows="5"
                placeholder={t('web.message')}
                className="bg-white/5 border border-white/10 rounded-xl p-3 md:col-span-2"
              />

              {formStatus && (
                <div
                  className={`md:col-span-2 rounded-xl px-4 py-3 text-sm ${
                    formStatus === getMessage('success')
                      ? 'bg-green-500/10 border border-green-500/30 text-green-300'
                      : formStatus === getMessage('sending')
                      ? 'bg-primary/10 border border-primary/30 text-primary'
                      : 'bg-red-500/10 border border-red-500/30 text-red-300'
                  }`}
                >
                  {formStatus}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className={`bg-gradient-to-r from-primary to-secondary rounded-xl p-3 font-bold md:col-span-2 transition ${
                  isSubmitting
                    ? 'opacity-60 cursor-not-allowed'
                    : 'hover:opacity-90'
                }`}
              >
                {isSubmitting
                  ? getMessage('sending')
                  : t('web.send')}
              </button>
            </form>
          </div>
        </section>
      </main>
    </div>
  );
}